import {
  getNomineesAnalyticsQuery,
  getNomineesSummaryQuery,
  getOverdueAndDueDatesQuery,
  getPaymentsConsolidatedQuery,
} from 'lib/api/fundsDashboard/fundsDashboard';
import type {
  FundPaymentsConsolidated,
  NomineesAnalyticsResponse,
  OverdueAndDueDatesResponse,
} from 'lib/api/fundsDashboard/types';
import type { NomineesSummary } from 'lib/api/fundsDashboard/types';
import React, { createContext, ReactNode, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useNomineesPeriod } from './NomineesPeriodContext';

export interface FundSummaryDataContextValue {
  nomineesSummary: NomineesSummary | undefined;
  nomineesAnalytics: NomineesAnalyticsResponse | undefined;
  overdueAndDueDates: OverdueAndDueDatesResponse | undefined;
  paymentsConsolidated: FundPaymentsConsolidated | undefined;

  isPendingNomineesSummary: boolean;
  isPendingNomineesAnalytics: boolean;
  isPendingOverdueAndDueDates: boolean;
  isPendingPaymentsConsolidated: boolean;
  isErrorNomineesSummary: boolean;
  isErrorNomineesAnalytics: boolean;
  isErrorOverdueAndDueDates: boolean;
  isErrorPaymentsConsolidated: boolean;

  refetchAll: () => Promise<void>;
}

export const FundSummaryDataContext =
  createContext<FundSummaryDataContextValue | null>(null);

interface FundSummaryDataProviderProps {
  children: ReactNode;
}

export const FundSummaryDataProvider: React.FC<
  FundSummaryDataProviderProps
> = ({ children }) => {
  const dispatch = useDispatch();
  const { startOperationDate, endOperationDate } = useNomineesPeriod();

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (startOperationDate)
      params.set('startOperationDate', startOperationDate);
    if (endOperationDate) params.set('endOperationDate', endOperationDate);
    return params.toString();
  }, [startOperationDate, endOperationDate]);

  const nomineesSummaryResult = getNomineesSummaryQuery(dispatch, query);
  const nomineesAnalyticsResult = getNomineesAnalyticsQuery(dispatch, query);
  const overdueAndDueDatesResult = getOverdueAndDueDatesQuery(dispatch, query);
  const paymentsConsolidatedResult = getPaymentsConsolidatedQuery(
    dispatch,
    query,
  );

  const refetchAll = useCallback(async () => {
    await Promise.all([
      nomineesSummaryResult.refetch(),
      nomineesAnalyticsResult.refetch(),
      overdueAndDueDatesResult.refetch(),
      paymentsConsolidatedResult.refetch(),
    ]);
  }, [
    nomineesSummaryResult.refetch,
    nomineesAnalyticsResult.refetch,
    overdueAndDueDatesResult.refetch,
    paymentsConsolidatedResult.refetch,
  ]);

  const value = useMemo<FundSummaryDataContextValue>(
    () => ({
      nomineesSummary: nomineesSummaryResult.data,
      nomineesAnalytics: nomineesAnalyticsResult.data,
      overdueAndDueDates: overdueAndDueDatesResult.data,
      paymentsConsolidated: paymentsConsolidatedResult.data,
      isPendingNomineesSummary: nomineesSummaryResult.isPending,
      isPendingNomineesAnalytics: nomineesAnalyticsResult.isPending,
      isPendingOverdueAndDueDates: overdueAndDueDatesResult.isPending,
      isPendingPaymentsConsolidated: paymentsConsolidatedResult.isPending,
      isErrorNomineesSummary: nomineesSummaryResult.isError ?? false,
      isErrorNomineesAnalytics: nomineesAnalyticsResult.isError ?? false,
      isErrorOverdueAndDueDates: overdueAndDueDatesResult.isError ?? false,
      isErrorPaymentsConsolidated: paymentsConsolidatedResult.isError ?? false,
      refetchAll,
    }),
    [
      nomineesSummaryResult.data,
      nomineesSummaryResult.isPending,
      nomineesSummaryResult.isError,
      nomineesAnalyticsResult.data,
      nomineesAnalyticsResult.isPending,
      nomineesAnalyticsResult.isError,
      overdueAndDueDatesResult.data,
      overdueAndDueDatesResult.isPending,
      overdueAndDueDatesResult.isError,
      paymentsConsolidatedResult.data,
      paymentsConsolidatedResult.isPending,
      paymentsConsolidatedResult.isError,
      refetchAll,
    ],
  );

  return (
    <FundSummaryDataContext.Provider value={value}>
      {children}
    </FundSummaryDataContext.Provider>
  );
};

export const useFundSummaryData = (): FundSummaryDataContextValue => {
  const context = React.useContext(FundSummaryDataContext);
  if (!context) {
    throw new Error(
      'useFundSummaryData must be used within a FundSummaryDataProvider',
    );
  }
  return context;
};
